import { Component, Input, OnInit } from '@angular/core';

import { Observable, finalize, forkJoin, of, shareReplay } from 'rxjs';

import { LayerService } from '@mirage-projects/cw-commons';
import { Layer, LayerReference, LayerTypeEnum, Project } from '@mirage-projects/cw-models';

export interface LayerInfo {
    title: string;
    data: Project['layerTree'] | undefined;
}

@Component({
    selector: 'cw-card-horizontal-tree',
    templateUrl: './card-horizontal-tree.component.html',
    styleUrls: ['./card-horizontal-tree.component.scss'],
    standalone: false,
})
export class CardHorizontalTreeComponent implements OnInit {
    /* Project data with layerTree information */
    @Input() projectData: Project | undefined;

    /* Layers grouped by type */
    infoData: LayerInfo[] = [];

    private layerDataCache: Record<number, Observable<Layer>> = {};
    private layersLoadedMap: Record<number, boolean> = {};

    constructor(private layerService: LayerService) {}

    ngOnInit(): void {
        this.getInfoDataFiltered();
    }

    /**
     * Get layer data when the accordion is opened
     * @param layers - layer tree
     */
    onToggle(layers: LayerInfo): void {
        this.getFullLayersData(layers.data as LayerReference[]);
    }

    /**
     * Group the layer tree data by layer type
     */
    getInfoDataFiltered(): void {
        const layerInfo = [
            {
                title: 'LAYER_INFO.POINTCLOUD',
                data: this.getPointCloudData(),
            },
            {
                title: 'LAYER_INFO.VECTORLAYER',
                data: this.getVectorLayerData(),
            },
            {
                title: 'LAYER_INFO.RASTERLAYER',
                data: this.getRasterLayerData(),
            },
        ];

        this.infoData = layerInfo.filter((layer) => layer.data !== undefined && layer.data.length);
    }

    /**
     * Extract point cloud data from layer tree
     */
    getPointCloudData(): LayerReference[] | undefined {
        return this.projectData?.layerTree?.filter((pointCloudLayers) => pointCloudLayers.layerType === LayerTypeEnum.pointCloud);
    }

    /**
     * Extract vector layer data from layer tree
     */
    getVectorLayerData(): LayerReference[] | undefined {
        return this.projectData?.layerTree?.filter((vectorLayer) => vectorLayer.layerType === LayerTypeEnum.vectorLayer);
    }

    /**
     * Extract raster layer data from layer tree
     */
    getRasterLayerData(): LayerReference[] | undefined {
        return this.projectData?.layerTree?.filter((rasterLayer) => rasterLayer.layerType === LayerTypeEnum.rasterLayer);
    }

    /**
     * Complete the layer reference data with the full layer data
     * @param layers Layer reference list to be completed
     */
    getFullLayersData(layers: LayerReference[]): void {
        layers.forEach((layer) => (this.layersLoadedMap[layer.id] = false));
        const layerRequests = layers.map((layer) => {
            if (!this.layerDataCache[layer.id]) {
                this.layerDataCache[layer.id] = this.getFullLayerData(layer).pipe(shareReplay(1));
            }
            return this.layerDataCache[layer.id];
        });
        forkJoin(layerRequests)
            .pipe(
                finalize(() => {
                    layers.forEach((layer) => (this.layersLoadedMap[layer.id] = true));
                })
            )
            .subscribe((layersData) => {
                layersData.forEach((layerData, index) => {
                    layers[index] = layerData;
                });
            });
    }

    /**
     * Check if some layers are still loading
     * @param layers - array of layers being loaded
     */
    isLayerTreeLoading(layers: (Layer | LayerReference)[] | undefined): boolean {
        if (!layers) {
            return true;
        }
        return layers.some((layer) => !this.layersLoadedMap[layer.id]);
    }

    /**
     * Return a (Layer | LayerReference) array as Layer array.
     * @param layers Layer list to be comfirmed as layers
     */
    asLayers(layers: (Layer | LayerReference)[] | undefined): Layer[] {
        if (!layers) {
            return [];
        }
        return layers as Layer[];
    }

    private getFullLayerData(layer: LayerReference): Observable<Layer> {
        const isIncompleteLayer = 'id' in layer && 'layerType' in layer && Object.keys(layer).length === 2;
        if (isIncompleteLayer) {
            return this.layerService.get(layer.id);
        }

        return of(layer as any);
    }
}
